import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Circle,
  Heading,
  Icon,
  Skeleton,
  Text,
  useToast,
} from '@chakra-ui/react';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  EventForRenderFragmentGQLType,
  useGetEventsLazyQueryGQLType,
  useGetUserQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import eventConfig from './domains';
import { FilterDatePicker } from 'packages/common-frontend/components/FilterDatePicker';
import {
  PaginationControl,
  usePaginationLimit,
} from 'packages/common-frontend/components/PaginationControl';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { FiEdit } from 'react-icons/fi';

const DefaultEvent = ({ event }: { event: EventForRenderFragmentGQLType }) => {
  const { actorName, created_at: createdAt, domainKey } = event;
  return (
    <Box
      display="flex"
      alignContent="center"
      borderBottom="1px"
      borderColor="gray.100"
      marginTop="10px"
      marginBottom="10px"
    >
      <Circle size="40px">
        <Icon as={FiEdit} />
      </Circle>
      <Box
        alignContent="center"
        display="flex"
        justifyContent="space-between"
        marginBottom="10px"
        padding="10px"
        width="100%"
      >
        <Text>
          <strong>{actorName}</strong>
          {` recorded an event in `}
          <strong>{domainKey}</strong>
        </Text>
        <Text fontSize="sm" color="gray">
          {commonDate.epochToLocaleDateString(createdAt, undefined, {
            ...DateTime.TIME_SIMPLE,
            timeZoneName: 'short',
          })}
        </Text>
      </Box>
    </Box>
  );
};

export const Event = ({ event }: { event: EventForRenderFragmentGQLType }) => {
  const { domainKey, actionKey, created_at: createdAt } = event;
  const domainConfig = eventConfig[domainKey];
  if (!domainConfig) {
    return <DefaultEvent event={event} />;
  }
  const actionConfig = domainConfig[actionKey];
  if (!actionConfig) {
    return <DefaultEvent event={event} />;
  }
  const ActionComponent = actionConfig.Component;
  const PanelComponent = actionConfig.PanelComponent || null;

  return (
    <AccordionItem border="none" isDisabled={!PanelComponent}>
      <AccordionButton
        _disabled={{
          opacity: 1,
          _hover: { backgroundColor: 'transparent', cursor: 'default' },
        }}
        _expanded={{
          backgroundColor: 'millet.50',
          borderWidth: '1px 1px 0',
          borderColor: 'gray.100',
        }}
        borderTop="none"
        borderBottom="1px"
        borderColor="gray.100"
      >
        <Circle size="40px">
          <Icon as={actionConfig.icon} />
        </Circle>
        <Box
          alignContent="center"
          display="flex"
          justifyContent="space-between"
          padding="10px"
          width="100%"
        >
          <ActionComponent event={event} />
          <Text fontSize="sm" color="gray">
            {commonDate.epochToLocaleDateString(createdAt, undefined, {
              ...DateTime.TIME_SIMPLE,
              timeZoneName: 'short',
            })}
          </Text>
        </Box>
      </AccordionButton>
      <AccordionPanel
        borderWidth="0 1px 1px"
        borderColor="gray.100"
        pt="10px"
        pl="65px"
      >
        {PanelComponent && <PanelComponent event={event} />}
      </AccordionPanel>
    </AccordionItem>
  );
};

const Events = ({
  eventsMap,
}: {
  eventsMap: { [key in Util.FullDateStr]: EventForRenderFragmentGQLType[] };
}) => {
  return (
    <>
      {_.map(
        eventsMap,
        (array: EventForRenderFragmentGQLType[], key: Util.FullDateStr) => {
          return (
            <Box key={`event-day-${key}`} mt="25px" mb="25px">
              <Heading as="h3" fontSize="2xl" mb="25px">
                {commonDate.fullDateStrToHumanDateString(key)}
              </Heading>
              <Accordion allowMultiple>
                {_.map<EventForRenderFragmentGQLType, React.ReactElement>(
                  array,
                  (eventForRender) => {
                    return (
                      <Event
                        key={`event-${eventForRender.id}`}
                        event={eventForRender}
                      />
                    );
                  },
                )}
              </Accordion>
            </Box>
          );
        },
      )}
    </>
  );
};

export const EventsTable = () => {
  const toast = useToast();
  const { pageLimit, setPageLimit, options } = usePaginationLimit();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const { loading: userLoading, data: userData } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });
  const [
    getEvents,
    { loading: eventsLoading, data: eventsData },
  ] = useGetEventsLazyQueryGQLType({});

  useEffect(() => {
    getEvents({
      variables: {
        limit: pageLimit,
        organizationId: userData?.user?.organizationId || '',
        startDate: commonDate.optionalDateToFullDateStr(startDate),
        endDate: commonDate.optionalDateToFullDateStr(endDate),
      },
    });
  }, [startDate, endDate, pageLimit, userData, getEvents]);

  if (userLoading || eventsLoading || !eventsData) {
    return <Skeleton data-testid="loading-deliverables-view" height="600px" />;
  }

  const {
    events: {
      nodes: events,
      pageInfo: { hasNextPage, hasPreviousPage, startCursor, endCursor },
    },
  } = eventsData;
  let eventsByDay: {
    [key in Util.FullDateStr]: EventForRenderFragmentGQLType[];
  } = {};
  if (events) {
    eventsByDay = _.groupBy(events, (event) => {
      const { created_at: createdAt } = event;
      return commonDate.epochMsToFullDateStr(createdAt);
    });
  }

  return (
    <Box height="100%">
      <Heading as="h2" mb="1rem">
        Event Log
      </Heading>
      <PaginationControl
        filters={
          <FilterDatePicker
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
          />
        }
        pageLimit={pageLimit}
        setPageLimit={setPageLimit}
        options={options}
        limitLabel="Events per page"
        hasPreviousPage={hasPreviousPage}
        hasNextPage={hasNextPage}
        onPrevious={async () => {
          getEvents({
            variables: {
              limit: pageLimit,
              organizationId: userData?.user?.organizationId || '',
              before: startCursor,
              startDate: commonDate.optionalDateToFullDateStr(startDate),
              endDate: commonDate.optionalDateToFullDateStr(endDate),
            },
          });
        }}
        onNext={async () => {
          getEvents({
            variables: {
              limit: pageLimit,
              organizationId: userData?.user?.organizationId || '',
              after: endCursor,
              startDate: commonDate.optionalDateToFullDateStr(startDate),
              endDate: commonDate.optionalDateToFullDateStr(endDate),
            },
          });
        }}
      >
        <>
          {_.isEmpty(events) || _.isEmpty(eventsByDay) ? (
            <Text>No events have been recorded</Text>
          ) : (
            <Events eventsMap={eventsByDay} />
          )}
        </>
      </PaginationControl>
    </Box>
  );
};
